import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getCurrencyDetails, updateCurrency, clearError, clearSuccess } from '../../../../redux/Slices/authSlice';
import { FaArrowRight } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import { useTitle } from '../../../../shared/hooks/TitleContext';

const EditCurrency = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const { id } = useParams();
  const { currencyDetails, currencies, isLoading, error, success } = useSelector((s) => s.auth);
  const [form, setForm] = useState({ name_en: '', name_ar: '', code: '', symbol: '', is_base_currency: false });
  const lastErrorRef = useRef({ message: null, time: 0 });
  const lastSuccessRef = useRef({ message: null, time: 0 });
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';

  useEffect(() => {
    setTitle('currencies.edit.title');
  }, [setTitle]);

  useEffect(() => {
    if (id) {
      dispatch(getCurrencyDetails(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    // Try to get data from details response OR fallback to list if available (for offline support)
    let dataToUse = null;

    if (currencyDetails && (String(currencyDetails.id) === String(id))) {
       dataToUse = currencyDetails;
    } else if (currencies && currencies.length > 0) {
       const found = currencies.find(c => String(c.id) === String(id));
       if (found) dataToUse = found;
    }

    if (dataToUse) {
      setForm({
        name_en: dataToUse.name_en || dataToUse.name || '',
        name_ar: dataToUse.name_ar || dataToUse.name || '',
        code: dataToUse.code || '',
        symbol: dataToUse.symbol || '',
        is_base_currency: dataToUse.is_base_currency || false
      });
    }
  }, [currencyDetails, currencies, id]);

  useEffect(() => {
    if (success) {
      const now = Date.now();
      const last = lastSuccessRef.current;
      if (!last.message || last.message !== success || now - last.time > 2000) {
        toast.success(success, { 
          rtl: isRTL,
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        lastSuccessRef.current = { message: success, time: now };
      }
      setTimeout(() => {
        dispatch(clearSuccess());
        navigate('/currencies');
      }, 1500);
    }
  }, [success, dispatch, navigate, isRTL]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { 
          rtl: isRTL,
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch, isRTL]);

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(updateCurrency({ id, data: form }));
  };

  if (isLoading && !currencyDetails) {
    return (
      <div style={{ 
        padding: '30px', 
        backgroundColor: '#14263b', 
        minHeight: 'calc(100vh - 80px)', 
        color: 'white', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        direction: dir
      }}>
        {t('common.loading', { defaultValue: 'Loading...' })}
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '30px', 
      backgroundColor: '#14263b', 
      minHeight: 'calc(100vh - 80px)', 
      color: 'white', 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      direction: dir
    }}>
      <div style={{ marginBottom: '10px', width: '100%', maxWidth: '750px' }}>
        <button
          onClick={() => navigate('/currencies')}
          style={{
            backgroundColor: '#666',
            color: 'white',
            border: 'none',
            padding: '10px',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '10px',
            fontSize: '14px',
            flexDirection: isRTL ? 'row' : 'row-reverse'
          }}
        >
          <FaArrowRight style={{ transform: isRTL ? 'rotate(180deg)' : 'none' }} />
          {t('common.back', { defaultValue: 'Back' })}
        </button>
        <h1 style={{ 
          fontSize: '24px', 
          marginBottom: '10px',
          textAlign
        }}>
          {t('currencies.edit.title', { defaultValue: 'Edit Currency' })}
        </h1>
      </div>
      <form onSubmit={handleSubmit} style={{ 
        backgroundColor: '#1b3350', 
        border: '1px solid #333', 
        borderRadius: '12px', 
        padding: '32px', 
        width: '100%', 
        maxWidth: '750px',
        direction: dir,
        pointerEvents: (isLoading || success) ? 'none' : 'auto',
        opacity: (isLoading || success) ? 0.7 : 1
      }}>
        <div style={{ marginBottom: '16px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            fontSize: '14px', 
            fontWeight: 'bold',
            textAlign,
            direction: dir
          }}>
            {t('currencies.form.nameEn', { defaultValue: 'Name (English)' })} <span style={{ color: '#dc3545' }}>*</span>
          </label>
          <input
            type="text"
            value={form.name_en}
            onChange={(e) => setForm({ ...form, name_en: e.target.value })}
            required
            style={{ 
              width: '100%', 
              padding: '12px', 
              backgroundColor: '#1a1f2e', 
              border: '1px solid #333', 
              borderRadius: '8px', 
              color: 'white', 
              boxSizing: 'border-box',
              direction: 'ltr',
              textAlign: 'left'
            }}
          />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            fontSize: '14px', 
            fontWeight: 'bold',
            textAlign,
            direction: dir
          }}>
            {t('currencies.form.nameAr', { defaultValue: 'Name (Arabic)' })} <span style={{ color: '#dc3545' }}>*</span>
          </label>
          <input
            type="text"
            value={form.name_ar}
            onChange={(e) => setForm({ ...form, name_ar: e.target.value })}
            required
            style={{ 
              width: '100%', 
              padding: '12px', 
              backgroundColor: '#1a1f2e', 
              border: '1px solid #333', 
              borderRadius: '8px', 
              color: 'white', 
              boxSizing: 'border-box',
              direction: 'rtl',
              textAlign: 'right'
            }}
          />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            fontSize: '14px', 
            fontWeight: 'bold',
            textAlign,
            direction: dir
          }}>
            {t('currencies.form.code', { defaultValue: 'Code' })} <span style={{ color: '#dc3545' }}>*</span>
          </label>
          <input
            type="text"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            required
            style={{ 
              width: '100%', 
              padding: '12px', 
              backgroundColor: '#1a1f2e', 
              border: '1px solid #333', 
              borderRadius: '8px', 
              color: 'white', 
              boxSizing: 'border-box',
              direction: dir,
              textAlign
            }}
          />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            fontSize: '14px', 
            fontWeight: 'bold',
            textAlign,
            direction: dir
          }}>
            {t('currencies.form.symbol', { defaultValue: 'Symbol' })} <span style={{ color: '#dc3545' }}>*</span>
          </label>
          <input
            type="text"
            value={form.symbol}
            onChange={(e) => setForm({ ...form, symbol: e.target.value })}
            required
            style={{ 
              width: '100%', 
              padding: '12px', 
              backgroundColor: '#1a1f2e', 
              border: '1px solid #333', 
              borderRadius: '8px', 
              color: 'white', 
              boxSizing: 'border-box',
              direction: dir,
              textAlign
            }}
          />
        </div>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            cursor: 'pointer',
            flexDirection: isRTL ? 'row' : 'row-reverse'
          }}>
            <input
              type="checkbox"
              checked={form.is_base_currency}
              onChange={(e) => setForm({ ...form, is_base_currency: e.target.checked })}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span style={{ 
              fontSize: '14px',
              textAlign,
              direction: dir
            }}>
              {t('currencies.form.isBase', { defaultValue: 'Base Currency' })}
            </span>
          </label>
        </div>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: '12px', 
          marginTop: '24px',
          flexDirection: isRTL ? 'row' : 'row-reverse'
        }}>
          <button 
            type="button" 
            onClick={() => navigate('/currencies')} 
            style={{ 
              background: '#666', 
              color: 'white', 
              border: 'none', 
              borderRadius: '8px', 
              padding: '10px 16px', 
              cursor: 'pointer' 
            }}
          >
            {t('currencies.form.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button 
            type="submit" 
            disabled={isLoading} 
            style={{ 
              background: '#AC2000', 
              color: 'white', 
              border: 'none', 
              borderRadius: '8px', 
              padding: '10px 16px', 
              cursor: 'pointer', 
              opacity: isLoading ? 0.7 : 1 
            }}
          >
            {isLoading ? t('currencies.form.saving', { defaultValue: 'Saving...' }) : t('currencies.form.save', { defaultValue: 'Save' })}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditCurrency;
